import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ACCEPT_ALL, checkZip, joinWaitlist } from '../lib/serviceArea'

// First screen for a logged-out visitor — spec §1.
// A single 5-digit ZIP input, verdict on the 5th character:
//   - accepted  → green box + Continue → /signup?zip=X&city=Y
//   - rejected  → amber box + email field for the waitlist
// The `ACCEPT_ALL` constant is the SINGLE knob that toggles the two flows.
// No condition duplicated in the JSX below sneaks around it.

// "Bronx" is the one service-area name that takes an article in English —
// "we pick up in Bronx" reads wrong. It stays plain in SERVICE_AREA because
// that same value prefills the signup form's City field, where "the Bronx"
// would be a bad address. So the article is added here, for display only.
const cityLabel = (city) => (city === 'Bronx' ? 'the Bronx' : city)

export default function ZipEntry() {
  const navigate = useNavigate()
  const [raw, setRaw] = useState('')
  const [waitlistEmail, setWaitlistEmail] = useState('')
  const [waitlistState, setWaitlistState] = useState('idle') // idle | sending | done | error
  const [waitlistError, setWaitlistError] = useState('')

  const result = checkZip(raw) // { valid, accepted, city, zip }
  const showResult = result.valid

  // Reset waitlist state each time the user types a new ZIP.
  useEffect(() => {
    setWaitlistState('idle')
    setWaitlistError('')
  }, [result.zip])

  function handleContinue() {
    if (!result.accepted) return
    const params = new URLSearchParams({ zip: result.zip })
    if (result.city) params.set('city', result.city)
    navigate(`/signup?${params.toString()}`)
  }

  async function handleWaitlist(e) {
    e.preventDefault()
    setWaitlistError('')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(waitlistEmail.trim())) {
      setWaitlistError('Enter a valid email address.')
      return
    }
    setWaitlistState('sending')
    const ok = await joinWaitlist(waitlistEmail, result.zip)
    if (ok) {
      setWaitlistState('done')
    } else {
      setWaitlistState('error')
      setWaitlistError('Could not save your email. Try again in a minute.')
    }
  }

  return (
    <div className="min-h-screen">
      <ZipHero />

      {/* The card slides up over the bottom of the hero. */}
      <div className="relative mx-auto -mt-8 max-w-md px-4 pb-10">
      <div className="rounded-2xl border border-line bg-white p-6 shadow-sm">
        <h1 className="font-bricolage text-[22px] font-extrabold text-navy">
          Do we pick up at your place?
        </h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
          Enter your ZIP — five digits. We'll tell you right away.
        </p>

        <input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="00000"
          aria-label="ZIP code"
          className={`mt-5 h-16 w-full rounded-xl border-[1.5px] bg-white text-center font-bricolage text-[26px] font-extrabold tracking-[0.35em] text-navy focus:outline-none focus:ring-2 focus:ring-aqua/40 ${
            showResult && !result.accepted ? 'border-red' : 'border-line focus:border-aqua'
          }`}
        />

        {showResult && result.accepted && (
          <div
            className="mt-4 rounded-lg px-4 py-3 text-[13px] leading-relaxed"
            style={{ background: '#E9F9EF', border: '1px solid #B6E7C7', color: '#065F46' }}
          >
            <b className="block">
              {result.city
                ? `Yes — we pick up in ${cityLabel(result.city)}.`
                : `Yes — we pick up at ${result.zip}.`}
            </b>
            Morning 9–11 AM and evening 6–9 PM, seven days a week.
          </div>
        )}

        {showResult && result.accepted && (
          <button
            type="button"
            onClick={handleContinue}
            className="mt-4 h-[52px] w-full rounded-xl bg-navy font-bricolage text-[15px] font-extrabold text-white"
          >
            Continue
          </button>
        )}

        {showResult && !result.accepted && (
          <>
            <div
              className="mt-4 rounded-lg px-4 py-3 text-[13px] leading-relaxed"
              style={{ background: '#FEF6DF', border: '1px solid #F0DCA0', color: '#7A5E0C' }}
            >
              <b className="block">Not in {result.zip} yet.</b>
              We're opening new areas every month — leave your email and you'll be the first to know.
            </div>

            {waitlistState === 'done' ? (
              <div
                className="mt-3 rounded-lg px-4 py-3 text-[13px] leading-relaxed"
                style={{ background: '#E9F9EF', border: '1px solid #B6E7C7', color: '#065F46' }}
              >
                <b className="block">You&apos;re on the list.</b>
                We&apos;ll email you as soon as we cover {result.zip}.
              </div>
            ) : (
              <form onSubmit={handleWaitlist} className="mt-3 space-y-2">
                <input
                  type="email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="field-input"
                  required
                />
                {waitlistError && (
                  <p className="text-[11px] font-bold text-red">{waitlistError}</p>
                )}
                <button
                  type="submit"
                  disabled={waitlistState === 'sending'}
                  className={`h-11 w-full rounded-xl bg-navy font-bricolage text-[13px] font-extrabold text-white ${waitlistState === 'sending' ? 'opacity-60' : ''}`}
                >
                  {waitlistState === 'sending' ? 'Adding…' : 'Notify me'}
                </button>
              </form>
            )}
          </>
        )}

        {/* Debug hint for the launch phase — ACCEPT_ALL doesn't need a UI,
            but it's the ONLY switch that controls the two branches above. */}
        {!ACCEPT_ALL && (
          <p className="mt-3 text-center text-[10px] text-ink-3">
            Coverage is limited during pilot. New ZIPs open regularly.
          </p>
        )}
      </div>

      <p className="mt-6 text-center text-[13px] text-ink-2">
        Already have an account?{' '}
        <Link to="/login" className="font-extrabold text-navy underline">
          Log in
        </Link>
      </p>
      </div>
    </div>
  )
}

// ── Hero ────────────────────────────────────────────────────────────────
// Top of the ZIP screen only. To add the video, put the clip in
// public/video/ and point HERO_VIDEO at it: 5–10 s, loops cleanly, NO audio
// track (iOS only autoplays muted video), under ~2 MB. HERO_POSTER is the
// still shown while it loads and to visitors who turned motion off.
// Both null → plain navy hero.
const HERO_VIDEO = null
const HERO_POSTER = null

function ZipHero() {
  const videoRef = useRef(null)
  // Plain read, not state: it only decides whether the <video> mounts.
  const reducedMotion =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  // React sets `muted` as a DOM property only, never as an attribute, and
  // iOS Safari looks for the attribute before it allows autoplay. Set both,
  // then start playback explicitly.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    v.muted = true
    v.defaultMuted = true
    v.setAttribute('muted', '')
    v.play?.().catch(() => {})
  }, [])

  return (
    <div className="relative h-[46vh] max-h-[420px] min-h-[280px] overflow-hidden bg-navy">
      {HERO_VIDEO && !reducedMotion && (
        <video
          ref={videoRef}
          src={HERO_VIDEO}
          poster={HERO_POSTER || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {HERO_VIDEO && reducedMotion && HERO_POSTER && (
        <img
          src={HERO_POSTER}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* Navy wash so the white logo stays readable on any footage. */}
      <div className="absolute inset-0" style={{ background: 'rgba(11,32,56,0.55)' }} />

      <div className="relative flex h-full flex-col items-center justify-center gap-4 pb-8 text-center">
        <div className="rounded-2xl bg-white p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
          <img
            src="/logo-ghost.png"
            alt=""
            aria-hidden="true"
            width="64"
            height="64"
            className="h-16 w-16"
          />
        </div>
        {/* Light variant of logo-wordmark.png — navy recoloured to white,
            aqua kept — so it reads on the dark hero. */}
        <img
          src="/logo-wordmark-light.png"
          alt="Ghost Laundry — we do your laundry"
          width="720"
          height="100"
          className="h-auto w-[240px] max-w-[70vw]"
        />
      </div>
    </div>
  )
}
